import React, { useState } from 'react';
import './DashboardCard.css';
import FullscreenCardModal from './FullscreenCardModal';
import { resolveCardIconAccent, type CardIconAccent, type CompactTileVariant } from './cardIconAccent';

export type { CompactTileVariant } from './cardIconAccent';

export type ExpandMode = 'inline' | 'fullscreen';
export type HeaderSize = 'normal' | 'narrow' | 'wide';
export type CollapsedPreviewVariant = 'finances' | 'shift' | 'my-shifts' | 'active-shifts' | 'accounting';

export interface DashboardCardProps {
  id: string;
  title: string;
  icon?: string;
  children: React.ReactNode;
  className?: string;
  isCollapsible?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onCardClick?: () => void;
  onTitleClick?: () => void;
  isSelected?: boolean;
  expandMode?: ExpandMode; // 'inline' - раскрытие на месте, 'fullscreen' - полноэкранный режим
  isFullscreenOpen?: boolean; // Управляемое состояние fullscreen (если передано)
  onFullscreenOpenChange?: (isOpen: boolean) => void; // Callback для отслеживания открытия fullscreen модального окна
  /** Не показывать свёрнутую карточку в списке (только fullscreen-модал) */
  hideCollapsedCard?: boolean;
  counters?: (number | string)[]; // Счетчики для отображения в заголовке в формате |100|50|
  countersLoading?: boolean; // Показывать skeleton loader для счетчиков
  /** Индикатор предупреждения: восклицательный знак и число (например, неуказанные активности) */
  warningCounter?: number;
  /** Всплывающая подсказка для индикатора предупреждения */
  warningCounterTitle?: string;
  headerSize?: HeaderSize; // 'normal' - обычный заголовок, 'narrow' - узкий по ширине/высокий по высоте, 'wide' - широкий по ширине/низкий по высоте
  headerActions?: React.ReactNode; // Дополнительные действия в заголовке (кнопки, иконки и т.д.)
  /** Компактная плитка в горизонтальном ряду на странице объекта */
  compactTileVariant?: CompactTileVariant;
  /** Цвет акцента иконки (иначе определяется по id карточки) */
  iconAccent?: CardIconAccent;
  /** Контент под заголовком в свёрнутом состоянии */
  collapsedPreview?: React.ReactNode;
  /** Стили превью свёрнутой карточки */
  collapsedPreviewVariant?: CollapsedPreviewVariant;
  /** Только превью: без разворота, контент скрыт */
  previewOnly?: boolean;
  /** При inline-раскрытии оставить превью сверху, контент — ниже (тот же стиль карточки) */
  keepCollapsedPreviewWhenExpanded?: boolean;
}

const DashboardCard: React.FC<DashboardCardProps> = ({
  id,
  title,
  icon,
  children,
  className = '',
  isCollapsible = false,
  isCollapsed: externalIsCollapsed,
  onToggleCollapse: externalOnToggleCollapse,
  onCardClick,
  onTitleClick,
  isSelected = false,
  expandMode = 'inline',
  isFullscreenOpen: externalIsFullscreenOpen,
  onFullscreenOpenChange,
  hideCollapsedCard = false,
  counters,
  countersLoading = false,
  warningCounter,
  warningCounterTitle = 'Активностей без вида работ',
  headerSize = 'normal',
  headerActions,
  compactTileVariant,
  iconAccent,
  collapsedPreview,
  collapsedPreviewVariant,
  previewOnly = false,
  keepCollapsedPreviewWhenExpanded = false,
}) => {
  // Внутреннее состояние сворачивания, если не передано внешнее
  const [internalIsCollapsed, setInternalIsCollapsed] = useState(true);
  // Состояние для полноэкранного режима
  const [internalIsFullscreenOpen, setInternalIsFullscreenOpen] = useState(false);
  const isFullscreenControlled = externalIsFullscreenOpen !== undefined;
  const isFullscreenOpen = isFullscreenControlled ? externalIsFullscreenOpen : internalIsFullscreenOpen;

  const setFullscreenOpen = React.useCallback((open: boolean) => {
    if (!isFullscreenControlled) {
      setInternalIsFullscreenOpen(open);
    }
    if (expandMode === 'fullscreen') {
      onFullscreenOpenChange?.(open);
    }
  }, [expandMode, isFullscreenControlled, onFullscreenOpenChange]);
  
  // Используем внешнее состояние, если передано, иначе внутреннее
  const isCollapsed = externalIsCollapsed !== undefined ? externalIsCollapsed : internalIsCollapsed;
  
  // Функция переключения состояния
  const handleToggleCollapse = () => {
    if (expandMode === 'fullscreen') {
      // В полноэкранном режиме открываем модальное окно (уведомление родителя через useEffect)
      setFullscreenOpen(true);
    } else {
      // В inline режиме работаем как обычно
      if (externalOnToggleCollapse) {
        externalOnToggleCollapse();
      } else {
        setInternalIsCollapsed(!internalIsCollapsed);
      }
    }
  };
  
  // Обработчик клика по заголовку в полноэкранном режиме
  const handleTitleClick = () => {
    if (expandMode === 'fullscreen') {
      setFullscreenOpen(true);
    } else if (onTitleClick) {
      onTitleClick();
    }
  };

  // В полноэкранном режиме всегда показываем карточку свернутой
  const displayCollapsed = previewOnly || expandMode === 'fullscreen' ? true : isCollapsed;
  const canToggleCollapse = !previewOnly && (isCollapsible || expandMode === 'fullscreen');
  const usePreviewShell =
    !!collapsedPreview &&
    !!collapsedPreviewVariant &&
    (displayCollapsed || (keepCollapsedPreviewWhenExpanded && !displayCollapsed));
  const showPreviewBlock =
    !!collapsedPreview &&
    (displayCollapsed || (keepCollapsedPreviewWhenExpanded && !displayCollapsed));
  
  // Стабильная ссылка на onClose - иначе FullscreenCardModal ре-рендерится при каждом обновлении
  // родителя (загрузка данных и т.д.) и эффект срабатывает как "закрытие", восстанавливая
  // обработчик ObjectPage, хотя модал ещё открыт
  const handleFullscreenClose = React.useCallback(() => {
    setFullscreenOpen(false);
  }, [setFullscreenOpen]);

  const resolvedIconAccent = resolveCardIconAccent(id, compactTileVariant, iconAccent);

  const renderCardIcon = () => {
    if (!icon || compactTileVariant) return null;
    return (
      <span
        className={`card-icon-wrap card-icon-wrap--header card-icon-wrap--accent-${resolvedIconAccent}`}
        aria-hidden
      >
        <i className={icon}></i>
      </span>
    );
  };

  const renderCounters = () => {
    if (!(countersLoading || (counters && counters.length > 0) || (warningCounter !== undefined && warningCounter > 0))) {
      return null;
    }

    return (
      <div className="card-counters">
        {countersLoading ? (
          <span className="card-counter card-counter--skeleton">
            <span className="card-counter-skeleton"></span>
          </span>
        ) : counters && counters.length > 0 ? (
          counters.map((counterVal, index) => (
            <span key={index} className="card-counter card-counter--loaded">
              {counterVal}
            </span>
          ))
        ) : null}
        {!countersLoading && warningCounter !== undefined && warningCounter > 0 && (
          <span className="card-counter card-counter--loaded card-counter--warning" title={warningCounterTitle}>
            <i className="ri-error-warning-line"></i> {warningCounter}
          </span>
        )}
      </div>
    );
  };
  
  return (
    <>
      {!hideCollapsedCard && (
      <div 
        className={`dashboard-card ${displayCollapsed ? 'collapsed' : ''} ${isSelected ? 'selected' : ''} ${headerSize === 'narrow' ? 'card-narrow' : ''} ${compactTileVariant ? `dashboard-card-compact-tile dashboard-card-compact-tile--${compactTileVariant}` : ''} ${usePreviewShell ? 'dashboard-card-has-preview' : ''} ${usePreviewShell && collapsedPreviewVariant ? `dashboard-card-preview--${collapsedPreviewVariant}` : ''} ${!displayCollapsed && keepCollapsedPreviewWhenExpanded ? 'dashboard-card-preview-expanded' : ''} ${previewOnly ? 'dashboard-card-preview-only' : ''} ${className}`} 
        data-card-id={id}
        onClick={onCardClick}
        style={{ cursor: onCardClick ? 'pointer' : 'default' }}
      >
        <div 
          className={`card-header ${canToggleCollapse ? 'clickable' : ''} ${headerSize !== 'normal' ? `card-header--${headerSize}` : ''} ${compactTileVariant ? 'card-header--compact-tile' : ''}`}
          onClick={(e) => {
            if (canToggleCollapse) {
              e.stopPropagation();
              handleToggleCollapse();
            }
          }}
          title={
            expandMode === 'fullscreen' 
              ? "Открыть в полноэкранном режиме" 
              : isCollapsible 
                ? (isCollapsed ? "Развернуть" : "Свернуть") 
                : undefined
          }
        >
          <div 
            className={`card-title ${compactTileVariant ? 'card-title--compact-tile' : ''} ${onTitleClick || expandMode === 'fullscreen' ? 'clickable-title' : ''}`}
            onClick={(e) => {
              if (expandMode === 'fullscreen' || onTitleClick) {
                e.stopPropagation();
                handleTitleClick();
              }
            }}
            style={{ cursor: (onTitleClick || expandMode === 'fullscreen') ? 'pointer' : 'default' }}
          >
            {renderCardIcon()}
            <h3>{title}</h3>
            {renderCounters()}
          </div>
          
          {!displayCollapsed && (
            <div className="card-actions" onClick={(e) => e.stopPropagation()}>
              {headerActions}
            </div>
          )}
        </div>

        {showPreviewBlock && (
          <div
            className={`card-collapsed-preview${collapsedPreviewVariant ? ` card-collapsed-preview--${collapsedPreviewVariant}` : ''}`}
            onClick={(e) => {
              if (canToggleCollapse) {
                e.stopPropagation();
                handleToggleCollapse();
              }
            }}
          >
            {collapsedPreview}
          </div>
        )}
        
        {expandMode === 'inline' && !previewOnly && (
          <div className={`card-content ${isCollapsed ? 'collapsed' : ''}`}>
            {keepCollapsedPreviewWhenExpanded ? (
              <div className="card-content-expand-inner">{children}</div>
            ) : (
              children
            )}
          </div>
        )}
      </div>
      )}
      
      {expandMode === 'fullscreen' && (
        <FullscreenCardModal
          isOpen={isFullscreenOpen}
          onClose={handleFullscreenClose}
          title={title}
          headerActions={headerActions}
        >
          {children}
        </FullscreenCardModal>
      )}
    </>
  );
};

export default DashboardCard;
